import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown as getLoanBreakdownSelector } from 'selectors/getLoanBreakdown';
import ChatWrapper from 'components/FinancialCheckup/AICoach/Wrapper';
import useApplyScoreGoal from 'hooks/useApplyScoreGoal';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData as getApplicationDataThunk, getAICoachGoals, getLoanBreakdown } from 'thunks';
import { CurrentFlow } from 'enums/CurrentFlow';

import GoalsSection from './GoalsSection';
import NextStepSection from './NextStepSection';
import YourGoalSection from './YourGoalSection';
import AccelerateYourGoalsSection from './AccelerateYourGoalsSection';
import UpcomingSection from './UpcomingSection';
import DashboardActionTiles from './DashboardActionTiles';
import YourPlanneryLoanSection from './YourPlanneryLoanSection';
import LoansHistory from './Loan/LoansHistory';

import styles from './FinancialDashboard.module.scss';

const DASHBOARD_VISITED_KEY = 'financial_dashboard_visited';

const FinancialDashboard = (): JSX.Element => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application } = useSelector(getApplicationData);
  const { breakdowns, isLoading } = useSelector(getLoanBreakdownSelector);
  const hasTrackedView = useRef(false);

  useApplyScoreGoal(application?.id, application?.borrowerId);

  useEffect(() => {
    if (application?.currentFlow === CurrentFlow.AICoach && !hasTrackedView.current) {
      hasTrackedView.current = true;
      const visitType = sessionStorage.getItem(DASHBOARD_VISITED_KEY) ? 'repeat' : 'initial';
      analytics.track('Dashboard Viewed', { visit: visitType });
      sessionStorage.setItem(DASHBOARD_VISITED_KEY, 'true');
    }
  }, [application?.currentFlow]);

  useEffect(() => {
    if (application?.id) {
      dispatchWithUnwrap(getApplicationDataThunk(application.id));
    }
  }, []);

  useEffect(() => {
    if (application?.id) {
      if (breakdowns.length === 0 && !isLoading) dispatch(getLoanBreakdown(application.id));
      dispatch(getAICoachGoals(application.id));
    }
  }, [application?.id]);

  const paidOffLoans = breakdowns.filter((loan) => !loan.nextPaymentDate);
  const getLoanHistoryProps = () => ({
    count: paidOffLoans.length,
    title: `Your Plannery loan${paidOffLoans.length > 1 ? 's' : ''}`,
  });

  return (
    <div className={styles.content}>
      <h2 className={styles.title}>Hi {application?.borrowerFirstName}</h2>
      <NextStepSection />
      <YourGoalSection />
      <GoalsSection />
      <AccelerateYourGoalsSection />
      <YourPlanneryLoanSection />
      <UpcomingSection />
      <DashboardActionTiles />
      {paidOffLoans.length > 0 && <LoansHistory {...getLoanHistoryProps()} />}
      <ChatWrapper />
    </div>
  );
};

export default FinancialDashboard;
